import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import dayjs from "dayjs"
import { fetchWidgetCart, restoreWidgetCart, widgetCartHoldSelector, widgetCartItemsSelector, widgetCartTokenSelector } from "store/experiences"
import { CART_REFETCH_GAP_MS } from "./constants"

const storageKey = (guideUuid) => `booking_widget_cart_${guideUuid}`

// Storage can be missing or blocked (private window, blocked site data, a third-party iframe):
// the cart then lives in memory only. Saved as { items, hold, cartToken }; a plain list, or one
// without a cartToken, is an older save whose items aren't held.
const readCart = (guideUuid) => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(storageKey(guideUuid)) || "null")
    if (Array.isArray(saved)) return { items: saved, hold: null, cartToken: null }
    return { items: Array.isArray(saved?.items) ? saved.items : [], hold: saved?.hold || null, cartToken: saved?.cartToken || null }
  } catch (error) {
    return { items: [], hold: null, cartToken: null }
  }
}

// Written only when it changed: an unchanged write would tell the other tabs again, and they would
// write it back.
const writeCart = (guideUuid, cart) => {
  try {
    const value = JSON.stringify(cart)
    if (window.localStorage.getItem(storageKey(guideUuid)) !== value) window.localStorage.setItem(storageKey(guideUuid), value)
  } catch (error) {
    // Nothing to do: the cart still works for this visit.
  }
}

// The saved cart without the trips whose date has passed.
const readCurrentCart = (guideUuid) => {
  const today = dayjs().format("YYYY-MM-DD")
  const saved = readCart(guideUuid)
  return { ...saved, items: saved.items.filter((item) => item?.experienceId && item.date >= today) }
}

// Keeps the widget's cart, its hold and the API cart's token across reloads, per guide. Trips whose
// date has passed are dropped when it loads, and the holds are then read again from the API (the
// timer and each item's status). Returns false until the saved cart is back in the store, so no
// screen shows an empty cart (or saves one over the saved cart) before then.
// Tabs of the same widget share the cart: one tab's change (an item added, changed or removed, the
// reservation extended, released or renewed, the cart paid) shows in the others at once, and a tab
// the guest comes back to reads the API's cart again. Nothing is polled.
const useWidgetCartStorage = (guideUuid) => {
  const dispatch = useDispatch()
  const items = useSelector(widgetCartItemsSelector)
  const hold = useSelector(widgetCartHoldSelector)
  const cartToken = useSelector(widgetCartTokenSelector)
  const [loadedFor, setLoadedFor] = useState(null)
  const isLoaded = loadedFor === guideUuid
  const lastFetchRef = useRef(0)

  useEffect(() => {
    if (!guideUuid) return
    dispatch(restoreWidgetCart(readCurrentCart(guideUuid)))
    setLoadedFor(guideUuid)
    lastFetchRef.current = Date.now()
    dispatch(fetchWidgetCart())
  }, [guideUuid, dispatch])

  useEffect(() => {
    if (!guideUuid) return undefined
    // Another tab saved the cart (it read the API for it): this tab takes it as it is. A null key is
    // the whole storage cleared.
    const handleStorage = (event) => {
      if (event.key === storageKey(guideUuid) || event.key === null) dispatch(restoreWidgetCart(readCurrentCart(guideUuid)))
    }
    const handleReturn = () => {
      if (document.visibilityState !== "visible" || Date.now() - lastFetchRef.current < CART_REFETCH_GAP_MS) return
      lastFetchRef.current = Date.now()
      dispatch(fetchWidgetCart())
    }
    window.addEventListener("storage", handleStorage)
    window.addEventListener("focus", handleReturn)
    document.addEventListener("visibilitychange", handleReturn)
    return () => {
      window.removeEventListener("storage", handleStorage)
      window.removeEventListener("focus", handleReturn)
      document.removeEventListener("visibilitychange", handleReturn)
    }
  }, [guideUuid, dispatch])

  useEffect(() => {
    if (isLoaded) writeCart(guideUuid, { items, hold, cartToken })
  }, [isLoaded, guideUuid, items, hold, cartToken])

  return isLoaded
}

export default useWidgetCartStorage
